import request from 'tools/request'
import {
  RiskScoreDashboardSummaryResponse,
  RiskScoreDashboardV3Response,
  RiskScoreDashboardReferencesV2Response,
  RiskScoreDashboardV2Response,
  RiskScoresByAssetsResponse,
  RiskScoresByAssetsLabelledResponse,
} from './types/dashboard'

export const getCarrierToken = async () => {
  const carrierToken: any = await request.post(
    `${process.env.REACT_APP_CARRIER_API_URL}authenticate`,
    {
      username: localStorage.getItem('userName'),
      password: localStorage.getItem('password'),
    }
  )
  return carrierToken?.data?.token
}

export const getRiskScores = async () => {
  const carrierToken: any = localStorage.getItem('authToken')
  const res = await request.get(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores`,
    {
      headers: {
        Authorization: carrierToken,
        'Content-Encoding': 'gzip'
      },
    }
  )
  return res?.data
}

export const getRiskScoresWithAssets = async () => {
  const carrierToken: any = localStorage.getItem('authToken')
  const carrierCodeType: string = localStorage.getItem('carrierCodeType') ?? ""
  const carrierCodeValue: string = localStorage.getItem('carrierCodeValue') ?? ""
  if (carrierCodeType === "" || carrierCodeValue === "") {
    return []
  }
  const url = `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores?codeType=${carrierCodeType}&carrierCode=${carrierCodeValue}&showAssetDetails=true`
  const res = await request.get(
    url,
    {
      headers: {
        Authorization: carrierToken,
        'Content-Encoding': 'gzip'
      },
    }
  )
  return res?.data
}

export const getNewRiskScores = async (): Promise<RiskScoreDashboardV2Response | undefined> => {
  const carrierToken: any = localStorage.getItem('authToken')
  const res = await request.get<RiskScoreDashboardV2Response>(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-dashboard`,
    {
      headers: {
        Authorization: carrierToken,
        'Content-Encoding': 'gzip'
      },
    }
  )

  return res?.data
}

// V3 API calls with pagination support
export const getRiskScoresDashboardV3 = async (params?: {
  tagSubCategoryIds?: string
  credentialStatus?: string
  includeUntagged?: boolean
  search?: string
  sortBy?: string
  sortDirection?: string
  page?: number
  size?: number
  includeSummary?: boolean
}): Promise<RiskScoreDashboardV3Response | undefined> => {
  const res = await request.get<RiskScoreDashboardV3Response>(
    `${process.env.REACT_APP_CARRIER_API_URL_V3}risk-scores-dashboard`,
    {
      params: {
        ...(params?.tagSubCategoryIds && { tagSubCategoryIds: params.tagSubCategoryIds }),
        ...(params?.credentialStatus && { credentialStatus: params.credentialStatus }),
        ...(params?.includeUntagged !== undefined && { includeUntagged: params.includeUntagged }),
        ...(params?.search && params.search.length >= 3 && { search: params.search }),
        ...(params?.sortBy && { sortBy: params.sortBy }),
        ...(params?.sortDirection && { sortDirection: params.sortDirection }),
        ...(params?.page !== undefined && { page: params.page }),
        ...(params?.size && { size: params.size }),
        ...(params?.includeSummary !== undefined && { includeSummary: params.includeSummary })
      }
    }
  )
  return res?.data
}

export const getRiskScoresSummaryV3 = async (params?: {
  tagSubCategoryIds?: string
  credentialStatus?: string
  includeUntagged?: boolean
}): Promise<RiskScoreDashboardSummaryResponse | undefined> => {
  const res = await request.get<RiskScoreDashboardSummaryResponse>(
    `${process.env.REACT_APP_CARRIER_API_URL_V3}risk-scores-dashboard/summary`,
    {
      params: {
        ...(params?.tagSubCategoryIds && { tagSubCategoryIds: params.tagSubCategoryIds }),
        ...(params?.credentialStatus && { credentialStatus: params.credentialStatus }),
        ...(params?.includeUntagged !== undefined && { includeUntagged: params.includeUntagged })
      }
    }
  )
  return res?.data
}

export const getRiskScoresDashboardAgentV3 = async (params?: {
  search?: string
  sortBy?: string
  sortDirection?: string
  page?: number
  size?: number
  includeSummary?: boolean
}): Promise<RiskScoreDashboardV3Response | undefined> => {
  const res = await request.get<RiskScoreDashboardV3Response>(
    `${process.env.REACT_APP_CARRIER_API_URL_V3}risk-scores-dashboard-agents`,
    {
      params: {
        ...(params?.search && params.search.length >= 3 && { search: params.search }),
        ...(params?.sortBy && { sortBy: params.sortBy }),
        ...(params?.sortDirection && { sortDirection: params.sortDirection }),
        ...(params?.page !== undefined && { page: params.page }),
        ...(params?.size && { size: params.size }),
        ...(params?.includeSummary !== undefined && { includeSummary: params.includeSummary })
      }
    }
  )
  return res?.data
}

export const getRiskScoresSummaryAgentV3 = async (_params?: any): Promise<RiskScoreDashboardSummaryResponse | undefined> => {
  const res = await request.get<RiskScoreDashboardSummaryResponse>(
    `${process.env.REACT_APP_CARRIER_API_URL_V3}risk-scores-dashboard-agents/summary`
  )
  return res?.data
}

export const getNewRiskScoresWithAssets = async (options?: {
  useKilometers?: boolean
}): Promise<RiskScoresByAssetsResponse | undefined> => {
  const params: {
    codeType: string | null
    carrierCode: string | null
    useKilometers?: boolean
  } = {
    codeType: localStorage.getItem('carrierCodeType'),
    carrierCode: localStorage.getItem('carrierCodeValue'),
    ...(options?.useKilometers !== undefined && { useKilometers: options.useKilometers }),
  }
  const res = await request.get<RiskScoresByAssetsResponse>(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-by-assets`,
    {
      params: params,
    }
  )
  return res?.data
}

export const getPercentiles = async (codeType?: string | null, codeValue?: string | null) => {
  var endpointRoute = `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-carrier`
  const params: any = {
    codeType: codeType,
    codeValue: codeValue,
    useLabels: false,
  }
  const res = await request.get(endpointRoute, { params: params })
  return res?.data
}

export const getRiskScoresByAssetsLabelled = async (
  isCarrierLogin: boolean,
  options?: { useKilometers?: boolean; includeSummary?: boolean; includeRiskScores?: boolean }
): Promise<RiskScoresByAssetsLabelledResponse | undefined> => {
  const params: any = isCarrierLogin
    ? {
      codeType: 'TCID',
      carrierCode: localStorage.getItem('companyId'),
    }
    : {
      codeType: localStorage.getItem('carrierCodeType'),
      carrierCode: localStorage.getItem('carrierCodeValue'),
    }
  params.useLabels = false
  if (options?.useKilometers !== undefined) {
    params.useKilometers = options.useKilometers
  }
  if (options?.includeSummary !== undefined) {
    params.includeSummary = options.includeSummary
  }
  if (options?.includeRiskScores !== undefined) {
    params.includeRiskScores = options.includeRiskScores
  }

  const res = await request.get<RiskScoresByAssetsLabelledResponse>(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-by-assets-labelled`,
    {
      params: params,
    }
  )
  return res?.data
}

export const getNewRiskScoresForAgent = async (): Promise<RiskScoreDashboardV2Response | undefined> => {
  const res = await request.get<RiskScoreDashboardV2Response>(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-dashboard-agents`)
  return res?.data
}

export const getSubtagsForFilter = async (categoryIds: string): Promise<RiskScoreDashboardReferencesV2Response | undefined> => {
  const res = await request.get<RiskScoreDashboardReferencesV2Response>(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-dashboard/references`,
    {
      params: {
        tagCategoryIds: categoryIds,
      }
    }
  )
  return res?.data
}

export const getRiskScoresFilteredByTag = async (tagIds: any, includeUntagged?: boolean, credentialStatus?: string) => {
  const res = await request.get(
    `${process.env.REACT_APP_CARRIER_API_URL_V2}risk-scores-dashboard`,
    {
      params: {
        tagSubCategoryIds: tagIds,
        includeUntagged,
        ...(credentialStatus ? { credentialStatus } : {})
      },
    }
  )
  return res?.data
}